// Воронка через CSS-bars. Базовые 2 этапа (visit/click) рендерятся
// всегда. Если у теории есть impressions_total > 0 — сверху добавляется
// этап «Показы» с конверсиями visits/impressions и clicks/impressions.

import type { FunnelStep } from '../../lib/types.ts';

const STEP_LABELS: Record<FunnelStep['eventType'], string> = {
  visit: 'Визит на сайт',
  click: 'Клик на «Вступить»',
};

interface Props {
  data: FunnelStep[] | undefined;
  loading: boolean;
  /** Сумма последних значений impressions по всем ссылкам теории. */
  impressionsTotal: number;
}

function formatNumber(n: number): string {
  return n.toLocaleString('ru-RU');
}

function pct(num: number, denom: number): string {
  if (denom <= 0) return '—';
  return `${Math.round((num / denom) * 1000) / 10}%`;
}

export function FunnelView({ data, loading, impressionsTotal }: Props): JSX.Element {
  if (loading) {
    return (
      <div className="space-y-2">
        {[0, 1].map((i) => (
          <div key={i} className="card animate-pulse">
            <div className="h-6 rounded bg-bg-elevated" />
          </div>
        ))}
      </div>
    );
  }
  if (!data) return <div className="text-fg-muted">Нет данных</div>;

  const visit = data.find((s) => s.eventType === 'visit') ?? {
    eventType: 'visit',
    total: 0,
    unique: 0,
  };
  const click = data.find((s) => s.eventType === 'click') ?? {
    eventType: 'click',
    total: 0,
    unique: 0,
  };
  const hasImpressions = impressionsTotal > 0;

  const isEmpty = !hasImpressions && visit.total === 0 && click.total === 0;
  if (isEmpty) {
    return (
      <div className="card text-center text-fg-muted">
        Событий пока нет. Когда первый человек зайдёт по ссылке — он появится здесь.
      </div>
    );
  }

  // База для ширины баров — самое верхнее значение из доступных
  // (impressions если есть, иначе unique visit).
  const top = hasImpressions ? impressionsTotal : visit.unique;
  const visitWidthPct = top > 0 ? Math.max((visit.unique / top) * 100, 4) : 0;
  const clickWidthPct = top > 0 ? Math.max((click.unique / top) * 100, 4) : 0;

  return (
    <div className="space-y-2">
      {hasImpressions ? (
        <div className="card">
          <div className="mb-2 flex items-center justify-between">
            <div className="text-sm font-medium">Показы</div>
            <div className="text-sm font-semibold tabular-nums">
              {formatNumber(impressionsTotal)}
            </div>
          </div>
          <div className="h-2 overflow-hidden rounded bg-bg-elevated">
            <div className="h-full bg-accent" style={{ width: '100%' }} />
          </div>
        </div>
      ) : null}

      <div className="card">
        <div className="mb-2 flex items-center justify-between">
          <div className="text-sm font-medium">{STEP_LABELS.visit}</div>
          <div className="flex items-baseline gap-3 text-sm">
            <span className="font-semibold tabular-nums">{formatNumber(visit.unique)}</span>
            <span className="text-xs text-fg-muted tabular-nums">
              {formatNumber(visit.total)} всего
            </span>
          </div>
        </div>
        <div className="h-2 overflow-hidden rounded bg-bg-elevated">
          <div
            className="h-full bg-accent transition-all duration-300"
            style={{ width: `${visitWidthPct}%` }}
          />
        </div>
        {hasImpressions ? (
          <div className="mt-1.5 text-xs text-fg-muted">
            Конверсия из показов: {pct(visit.unique, impressionsTotal)}
          </div>
        ) : null}
      </div>

      <div className="card">
        <div className="mb-2 flex items-center justify-between">
          <div className="text-sm font-medium">{STEP_LABELS.click}</div>
          <div className="flex items-baseline gap-3 text-sm">
            <span className="font-semibold tabular-nums">{formatNumber(click.unique)}</span>
            <span className="text-xs text-fg-muted tabular-nums">
              {formatNumber(click.total)} всего
            </span>
          </div>
        </div>
        <div className="h-2 overflow-hidden rounded bg-bg-elevated">
          <div
            className="h-full bg-accent transition-all duration-300"
            style={{ width: `${clickWidthPct}%` }}
          />
        </div>
        <div className="mt-1.5 space-y-0.5 text-xs text-fg-muted">
          <div>Конверсия из визитов: {pct(click.unique, visit.unique)}</div>
          {hasImpressions ? (
            <div>Конверсия из показов: {pct(click.unique, impressionsTotal)}</div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
